// apps/client/src/pages/reports/SharedTrailBookValuationPage.tsx
// PUBLIC SHARED TRAIL BOOK VALUATION - Read-only view with VALUTTEN branding

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Loader2, AlertCircle, TrendingUp, Calendar, ArrowLeft } from 'lucide-react';
import { resolveTemplateDisplayName } from '../../utils/templateDisplayName';
import { COMMISSION_GST_BASIS } from '../../utils/commissionDisplay';
import { formatCompactCurrency } from '../../utils/formatters';

interface TemplateValuation {
  templateId: string;
  templateName: string | null;
  trailIncomeAnnualisedCents: number;
}

interface ValuationData {
  shareId: string;
  companyName: string;
  createdAt: string;
  year: number;
  yearType: 'calendar' | 'financial';
  multiplier: number;
  templates: TemplateValuation[];
  totalTrailIncomeCents: number;
  totalPresentValueCents: number;
  monthsWithData: number;
}

// Format currency from cents
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

// Currency shorthand (e.g., $1.23M): the app-wide three-significant-figure rule.
const formatCurrencyShorthand = (cents: number): string => formatCompactCurrency(cents);

const SharedTrailBookValuationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ValuationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const validateFunc = httpsCallable<
          { token: string },
          ValuationData
        >(functions, 'validateTrailBookShareToken');

        const response = await validateFunc({ token });
        setData(response.data);
      } catch (err: any) {
        console.error('Failed to validate share token:', err);

        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Valuation report not found');
        } else {
          setError(err.message || 'Failed to load shared valuation report');
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading valuation report...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Format the creation date
  const createdDate = new Date(data.createdAt);
  const formattedDate = createdDate.toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formattedTime = createdDate.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Format year display
  const yearDisplay = data.yearType === 'financial'
    ? `FY${data.year - 1}/${data.year.toString().slice(-2)}`
    : `${data.year}`;

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="p-8">
        {/* Header with VALUTTEN Branding */}
        <div className="rounded-2xl p-8 mb-8 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-600 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl animate-pulse delay-700" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-10 h-10 text-white" />
              <h1 className="text-4xl font-bold text-white">
                Trail Book Valuation
              </h1>
            </div>
            <p className="text-white/90 text-lg mb-2">
              {data.companyName}
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Shared Report - Read Only
              </div>
              <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm text-white/90">
                Powered by VALUTTEN
              </div>
            </div>
          </div>
        </div>

        {/* Report Metadata */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Report Generated</p>
                <p className="text-white font-medium">{formattedDate}</p>
                <p className="text-sm text-slate-500">{formattedTime}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400">Period</p>
              <p className="text-white font-medium text-lg">
                {yearDisplay} ({data.yearType === 'financial' ? 'Financial' : 'Calendar'} Year)
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Valuation Multiplier</p>
              <p className="text-white font-medium text-lg">{data.multiplier}x</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="text-slate-400 text-sm">Total Trail Income (Annualised)</h3>
            </div>
            <p className="text-3xl font-bold text-white">
              {formatCurrencyShorthand(data.totalTrailIncomeCents)}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Based on {data.monthsWithData} month{data.monthsWithData !== 1 ? 's' : ''} of data
              {data.monthsWithData < 12 ? ', extrapolated to 12 months' : ''}
              {` • ${COMMISSION_GST_BASIS}`}
            </p>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-slate-400 text-sm">Total Present Value ({data.multiplier}x)</h3>
            </div>
            <p className="text-3xl font-bold text-white">
              {formatCurrencyShorthand(data.totalPresentValueCents)}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {data.multiplier}x multiplier valuation{` • ${COMMISSION_GST_BASIS}`}
            </p>
          </div>
        </div>

        {/* Valuation Table */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden mb-8">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                  Trail Book
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">
                  Trail Income (Annualised)
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">
                  Present Value ({data.multiplier}x)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.templates.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-slate-400">
                    No trail income data available
                  </td>
                </tr>
              ) : (
                <>
                  {data.templates.map((template, index) => (
                    <tr
                      key={template.templateId || index}
                      className="hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-6 py-4 text-white font-medium">
                        {resolveTemplateDisplayName({
                          templateName: template.templateName,
                          id: template.templateId,
                        })}
                      </td>
                      <td className="px-6 py-4 text-right text-white">
                        {formatCurrency(template.trailIncomeAnnualisedCents)}
                      </td>
                      <td className="px-6 py-4 text-right text-white font-semibold">
                        {formatCurrency(template.trailIncomeAnnualisedCents * data.multiplier)}
                      </td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr className="bg-slate-900/50 font-bold">
                    <td className="px-6 py-4 text-white">Total</td>
                    <td className="px-6 py-4 text-right text-white">
                      {formatCurrency(data.totalTrailIncomeCents)}
                    </td>
                    <td className="px-6 py-4 text-right text-white">
                      {formatCurrency(data.totalPresentValueCents)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Methodology Note */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-8">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Valuation Methodology</h3>
          <p className="text-sm text-slate-400">
            This report calculates the present value of trail commission income using a {data.multiplier}x multiplier.
            Trail Income (Annualised) is extrapolated from {data.monthsWithData} month{data.monthsWithData !== 1 ? 's' : ''} of actual data to a full 12-month period
            {data.monthsWithData < 12 ? ` (factor: ${(12 / data.monthsWithData).toFixed(2)}x)` : ''}.
            Present Value is calculated as Annualised Trail Income × {data.multiplier}. Industry standard multipliers typically range from 4x to 6x.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center py-6 text-slate-500 text-sm border-t border-slate-700/50">
          <p>This is a shared, read-only view of the valuation report.</p>
          <p className="mt-2">
            <a
              href="https://valutten.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              Powered by VALUTTEN
            </a>
            {' '}&bull;{' '}
            Commission tracking and analytics for Australian mortgage brokers
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedTrailBookValuationPage;
